import { useState, useEffect, useCallback, useRef } from 'react';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import { journalEntriesApi } from '../services/api/endpoints/journalEntries';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { setOnEntryValidatedCallback } from './useAgentEntries';
import { useJournalActivity } from './useActivityTracking';
import type { JournalEntry } from '../types/accounting';

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function useJournalEntries() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({ total: 0, page: 1, pageSize: 20, totalPages: 1 });
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  const { logJournalEntry, logJournalUpdate, logJournalDelete, logJournalStateChange } = useJournalActivity();

  // Compteur de requête pour annuler les chargements obsolètes (race condition)
  const loadRequestIdRef = useRef(0);
  // Paramètres de pagination pour la prochaine requête API
  const paginationParamsRef = useRef<{ page: number; pageSize: number }>({ page: 1, pageSize: 20 });

  // Fonction pour ajouter une entrée validée par l'agent au journal
  const addValidatedEntry = useCallback((entry: JournalEntry) => {
    setEntries(prev => {
      // Vérifier que l'entrée n'existe pas déjà (éviter les doublons)
      if (prev.some(e => e.id === entry.id)) {
        console.log(`Entrée ${entry.id} existe déjà, mise à jour`);
        return prev.map(e => e.id === entry.id ? entry : e);
      }
      console.log(`Entrée ${entry.id} ajoutée au journal principal`);
      return [entry, ...prev];
    });
  }, []);

  // S'abonner aux notifications d'entrées validées par l'agent
  useEffect(() => {
    setOnEntryValidatedCallback(addValidatedEntry);
    return () => {
      setOnEntryValidatedCallback(() => {}); // Cleanup
    };
  }, [addValidatedEntry]);

  // Chargement mémoïsé avec gestion de concurrence (abort stale requests)
  const loadEntries = useCallback(async (pageOverride?: number, pageSizeOverride?: number) => {
    // Incrémenter le compteur pour invalider les requêtes précédentes
    const requestId = ++loadRequestIdRef.current;
    
    // Utiliser les paramètres fournis ou ceux stockés
    const reqPage = pageOverride ?? paginationParamsRef.current.page;
    const reqPageSize = pageSizeOverride ?? paginationParamsRef.current.pageSize;

    try {
      // ⚡ Immédiatement vider les données de l'ancien exercice pour éviter le flash
      setEntries([]);
      setLoading(true);
      setError(null);

      const fiscalYearId = currentFiscalYear?.id;
      
      // Charger depuis IndexedDB d'abord (pour le mode offline)
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');
      
      // Vérifier que cette requête est encore valide (pas de changement de FY entre-temps)
      if (requestId !== loadRequestIdRef.current) return;

      // Filtrer par exercice comptable si défini
      let filteredLocalEntries = fiscalYearId 
        ? localEntries.filter(e => e.fiscalYearId === fiscalYearId)
        : localEntries;
      
      // Filtrer par unité commerciale si définie
      if (effectiveFilter.businessUnitId) {
        filteredLocalEntries = filteredLocalEntries.filter(e => 
          (e as any).businessUnitId === effectiveFilter.businessUnitId ||
          effectiveFilter.businessUnitIds?.includes((e as any).businessUnitId) ||
          !(e as any).businessUnitId // Écritures sans unité sont visibles partout
        );
      }
      
      if (filteredLocalEntries.length > 0 && !navigator.onLine) {
        setEntries(filteredLocalEntries);
        setPagination({
          total: filteredLocalEntries.length,
          page: 1,
          pageSize: reqPageSize,
          totalPages: Math.ceil(filteredLocalEntries.length / reqPageSize)
        });
        setLoading(false);
      }

      // Si en ligne, synchroniser avec l'API
      if (navigator.onLine) {
        console.log(`📡 Chargement API journal entries pour FY: ${fiscalYearId || 'tous'}, page: ${reqPage}, pageSize: ${reqPageSize}`);
        const response = await journalEntriesApi.getAll({ 
          fiscalYearId,
          businessUnitId: effectiveFilter.businessUnitId,
          businessUnitType: effectiveFilter.businessUnitType,
          page: reqPage,
          pageSize: reqPageSize
        });

        // Vérifier que cette requête est encore valide
        if (requestId !== loadRequestIdRef.current) {
          console.log('⏭️ Requête journal entries annulée (FY changé entre-temps)');
          return;
        }

        if (response.success && response.data) {
          // L'API retourne { data: { data: JournalEntry[], total, page, pageSize, totalPages } }
          // ou { data: JournalEntry[], total, page, ... } selon le wrapper
          const paginatedData = response.data;
          
          if (paginatedData && Array.isArray(paginatedData.data)) {
            // Normaliser les attachments: backend retourne fileSize, frontend utilise size
            const normalizedEntries = paginatedData.data.map((entry: any) => ({
              ...entry,
              attachments: entry.attachments?.map((att: any) => ({
                ...att,
                size: att.size ?? att.fileSize,
              })) || []
            }));
            setEntries(normalizedEntries);
            
            // Extraire les métadonnées de pagination
            setPagination({
              total: paginatedData.total ?? paginatedData.data.length,
              page: paginatedData.page ?? reqPage,
              pageSize: paginatedData.pageSize ?? reqPageSize,
              totalPages: paginatedData.totalPages ?? Math.ceil((paginatedData.total ?? paginatedData.data.length) / reqPageSize)
            });
            
            // Mettre à jour IndexedDB (cache local)
            for (const entry of normalizedEntries) {
              await IndexedDBService.update('journal_entries', entry);
            }
          } else {
            console.warn('API response does not contain valid paginated data:', response.data);
            if (filteredLocalEntries.length === 0) {
              setEntries([]);
            }
          }
        } else {
          console.warn('API response failed or has no data:', response);
          if (filteredLocalEntries.length === 0) {
            setEntries([]);
          }
        }
      }
    } catch (error) {
      // Si la requête est obsolète, ignorer l'erreur
      if (requestId !== loadRequestIdRef.current) return;
      console.error('Failed to load journal entries:', error);
      setError('Erreur lors du chargement des écritures');
      setEntries([]);
    } finally {
      // Seulement mettre loading = false si c'est toujours la requête active
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentFiscalYear?.id, effectiveFilter.businessUnitId, effectiveFilter.businessUnitIds, effectiveFilter.businessUnitType]);

  /** Changer de page (déclenche un rechargement depuis l'API) */
  const setPage = useCallback((page: number) => {
    paginationParamsRef.current.page = page;
    loadEntries(page);
  }, [loadEntries]);

  /** Changer la taille de page (déclenche un rechargement depuis l'API) */
  const setPageSize = useCallback((pageSize: number) => {
    paginationParamsRef.current = { page: 1, pageSize };
    loadEntries(1, pageSize);
  }, [loadEntries]);

  // Recharger quand l'exercice comptable ou l'unité commerciale change
  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const addEntry = async (entry: Partial<JournalEntry>): Promise<void> => {
    try {
      // Vérifier qu'un exercice comptable est sélectionné
      if (!currentFiscalYear) {
        throw new Error('Aucun exercice comptable sélectionné. Veuillez sélectionner un exercice avant de créer une écriture.');
      }

      // Vérifier que l'exercice est ouvert
      if (currentFiscalYear.status !== 'open') {
        throw new Error('L\'exercice comptable est clôturé. Impossible de créer de nouvelles écritures.');
      }

      const newEntry: JournalEntry = {
        ...entry,
        id: crypto.randomUUID(),
        fiscalYearId: currentFiscalYear.id, // Associer à l'exercice comptable courant
        // Associer à l'unité commerciale sélectionnée (si définie)
        ...(selectedBusinessUnit && {
          businessUnitId: selectedBusinessUnit.id,
          businessUnitType: selectedBusinessUnit.type,
          businessUnitName: selectedBusinessUnit.name
        }),
        status: 'draft',
        source: 'manual',
        totalDebit: entry.lines?.reduce((sum, line) => sum + (line.debit || 0), 0) || 0,
        totalCredit: entry.lines?.reduce((sum, line) => sum + (line.credit || 0), 0) || 0,
        totalVat: entry.lines?.reduce((sum, line) => sum + (line.vatAmount || 0), 0) || 0,
        attachments: []
      } as JournalEntry;

      // Sauvegarder localement
      await IndexedDBService.add('journal_entries', newEntry);
      setEntries(prev => [newEntry, ...prev]);

      // Logger l'activité
      logJournalEntry('Nouvelle écriture créée', newEntry.description || newEntry.reference || 'Sans description', newEntry.totalDebit);

      // Ajouter à la file de synchronisation
      await SyncService.addToSyncQueue({
        id: newEntry.id,
        type: 'CREATE',
        entity: 'journal_entries',
        data: newEntry
      });
    } catch (error) {
      console.error('Failed to add journal entry:', error);
      throw error;
    }
  };

  const updateEntry = async (id: string, updates: Partial<JournalEntry>): Promise<void> => {
    try {
      // Protection contre les données undefined
      const safeEntries = Array.isArray(entries) ? entries : [];
      const existingEntry = safeEntries.find(e => e.id === id);
      if (!existingEntry) {
        throw new Error(`Entry with id ${id} not found`);
      }
      
      const updatedEntry: JournalEntry = { ...existingEntry, ...updates };
      
      // Mettre à jour localement (objet complet pour l'UI)
      await IndexedDBService.update('journal_entries', updatedEntry);
      setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

      // Logger l'activité
      logJournalUpdate(updatedEntry.description || updatedEntry.reference || 'Écriture mise à jour');

      // Préparer les données pour le backend (UpdateJournalDto — forbidNonWhitelisted)
      // N'envoyer que les champs acceptés par le DTO + mapper size -> fileSize pour les attachments
      const dtoData: Record<string, any> = { id };
      if (updates.date !== undefined) dtoData.date = updates.date;
      if (updates.journalType !== undefined) dtoData.journalType = updates.journalType;
      if (updates.description !== undefined) dtoData.description = updates.description;
      if (updates.reference !== undefined) dtoData.reference = updates.reference;
      if (updates.status !== undefined) dtoData.status = updates.status;
      if (updates.lines !== undefined) dtoData.lines = updates.lines;
      if (updates.businessUnitId !== undefined) dtoData.businessUnitId = updates.businessUnitId;
      if (updates.metadata !== undefined) dtoData.metadata = updates.metadata;
      if (updates.attachments !== undefined) {
        dtoData.attachments = updates.attachments.map(att => ({
          id: att.id,
          name: att.name,
          url: att.url,
          localUrl: att.localUrl,
          mimeType: att.mimeType,
          fileSize: att.size, // Frontend uses 'size', backend DTO expects 'fileSize'
          status: att.status,
        }));
      }

      // Ajouter à la file de synchronisation
      await SyncService.addToSyncQueue({
        id: updatedEntry.id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: dtoData
      });
    } catch (error) {
      console.error('Failed to update journal entry:', error);
      throw error;
    }
  };

  const deleteEntry = async (id: string): Promise<void> => {
    try {
      // Récupérer l'entrée avant suppression pour le log
      const safeEntries = Array.isArray(entries) ? entries : [];
      const entryToDelete = safeEntries.find(e => e.id === id);
      
      // Supprimer localement
      await IndexedDBService.delete('journal_entries', id);
      setEntries(prev => prev.filter(e => e.id !== id));

      // Logger l'activité
      if (entryToDelete) {
        logJournalDelete(entryToDelete.description || entryToDelete.reference || 'Écriture supprimée');
      }

      // Ajouter à la file de synchronisation
      await SyncService.addToSyncQueue({
        id,
        type: 'DELETE',
        entity: 'journal_entries',
        data: { id }
      });
    } catch (error) {
      console.error('Failed to delete journal entry:', error);
      throw error;
    }
  };

  const uploadAttachment = async (entryId: string, file: File): Promise<void> => {
    try {
      // Protection contre les données undefined
      const safeEntries = Array.isArray(entries) ? entries : [];
      const entry = safeEntries.find(e => e.id === entryId);
      if (!entry) {
        throw new Error(`Entry with id ${entryId} not found`);
      }

      // Simuler l'upload du fichier - dans une vraie app, ça irait vers un serveur
      const attachment = {
        id: crypto.randomUUID(),
        name: file.name,
        url: URL.createObjectURL(file), // En prod, ce serait l'URL du serveur
        status: 'uploaded' as const
      };

      const updatedEntry: JournalEntry = {
        ...entry,
        attachments: [...(entry.attachments || []), attachment]
      };

      await updateEntry(entryId, { attachments: updatedEntry.attachments });
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      throw error;
    }
  };

  const downloadAttachment = async (entryId: string, attachmentId: string): Promise<void> => {
    try {
      // Protection contre les données undefined
      const safeEntries = Array.isArray(entries) ? entries : [];
      const entry = safeEntries.find(e => e.id === entryId);
      const attachment = entry?.attachments?.find(att => att.id === attachmentId);
      
      if (!attachment?.url) {
        throw new Error('Attachment not found or no URL available');
      }

      // Simuler le téléchargement - en prod, ça téléchargerait depuis le serveur
      const link = document.createElement('a');
      link.href = attachment.url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      throw error;
    }
  };

  // ============================================
  // FONCTIONS DE TRANSITION D'ÉTAT
  // ============================================

  /**
   * Fonction utilitaire pour rollback en cas d'échec API
   */
  const rollbackEntry = async (originalEntry: JournalEntry) => {
    await IndexedDBService.update('journal_entries', originalEntry);
    setEntries(prev => prev.map(e => e.id === originalEntry.id ? originalEntry : e));
  };

  /**
   * Soumettre une écriture brouillon pour validation
   * Transition: draft → pending
   */
  const submitEntry = async (id: string): Promise<void> => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entry = safeEntries.find(e => e.id === id);
    
    if (!entry) {
      throw new Error(`Écriture ${id} non trouvée`);
    }

    if (entry.status !== 'draft') {
      throw new Error('Seuls les brouillons peuvent être soumis pour validation');
    }

    // Vérifier que l'écriture est équilibrée
    if (entry.totalDebit !== entry.totalCredit) {
      throw new Error('L\'écriture doit être équilibrée (débit = crédit)');
    }

    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entry };
    
    const updatedEntry: JournalEntry = {
      ...entry,
      status: 'pending'
    };

    // Mettre à jour localement de manière optimiste
    await IndexedDBService.update('journal_entries', updatedEntry);
    setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

    // Logger l'activité
    logJournalStateChange('Écriture soumise', `${entry.reference || entry.description || 'Écriture'} - En attente de validation`);

    // Synchroniser avec l'API si en ligne
    if (navigator.onLine) {
      try {
        await journalEntriesApi.submit(id);
      } catch (apiError) {
        console.error('API submit failed, rolling back:', apiError);
        // Rollback à l'état original
        await rollbackEntry(originalEntry);
        throw new Error('Échec de la soumission. L\'écriture a été restaurée à son état initial.');
      }
    } else {
      // Hors ligne: ajouter à la queue de sync
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: updatedEntry
      });
    }
  };

  /**
   * Approuver une écriture en attente
   * Transition: pending → approved
   */
  const approveEntry = async (id: string): Promise<void> => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entry = safeEntries.find(e => e.id === id);
    
    if (!entry) {
      throw new Error(`Écriture ${id} non trouvée`);
    }

    if (entry.status !== 'pending') {
      throw new Error('Seules les écritures en attente peuvent être approuvées');
    }

    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entry };
    
    const updatedEntry: JournalEntry = {
      ...entry,
      status: 'approved'
    };

    await IndexedDBService.update('journal_entries', updatedEntry);
    setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

    // Logger l'activité
    logJournalStateChange('Écriture approuvée', `${entry.reference || entry.description || 'Écriture'} - Prête pour comptabilisation`);

    if (navigator.onLine) {
      try {
        await journalEntriesApi.approve(id);
      } catch (apiError) {
        console.error('API approve failed, rolling back:', apiError);
        await rollbackEntry(originalEntry);
        throw new Error('Échec de l\'approbation. L\'écriture a été restaurée à son état initial.');
      }
    } else {
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: updatedEntry
      });
    }
  };

  /**
   * Rejeter une écriture en attente (retour au brouillon)
   * Transition: pending → draft
   */
  const rejectEntry = async (id: string, reason: string): Promise<void> => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entry = safeEntries.find(e => e.id === id);
    
    if (!entry) {
      throw new Error(`Écriture ${id} non trouvée`);
    }

    if (entry.status !== 'pending') {
      throw new Error('Seules les écritures en attente peuvent être rejetées');
    }

    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entry };
    
    const updatedEntry: JournalEntry = {
      ...entry,
      status: 'draft',
      // On pourrait ajouter un champ rejectionReason si nécessaire
    };

    await IndexedDBService.update('journal_entries', updatedEntry);
    setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

    // Logger l'activité
    logJournalStateChange('Écriture rejetée', `${entry.reference || entry.description || 'Écriture'} - ${reason}`, 'warning');

    if (navigator.onLine) {
      try {
        await journalEntriesApi.reject(id, reason);
      } catch (apiError) {
        console.error('API reject failed, rolling back:', apiError);
        await rollbackEntry(originalEntry);
        throw new Error('Échec du rejet. L\'écriture a été restaurée à son état initial.');
      }
    } else {
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: updatedEntry
      });
    }
  };

  /**
   * Comptabiliser une écriture approuvée
   * Transition: approved → posted
   */
  const postEntry = async (id: string): Promise<void> => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entry = safeEntries.find(e => e.id === id);
    
    if (!entry) {
      throw new Error(`Écriture ${id} non trouvée`);
    }

    if (entry.status !== 'approved') {
      throw new Error('Seules les écritures approuvées peuvent être comptabilisées');
    }

    // Vérifier que l'exercice est toujours ouvert
    if (currentFiscalYear?.status !== 'open') {
      throw new Error('L\'exercice comptable est clôturé');
    }

    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entry };
    
    const updatedEntry: JournalEntry = {
      ...entry,
      status: 'posted'
    };

    await IndexedDBService.update('journal_entries', updatedEntry);
    setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

    // Logger l'activité
    logJournalStateChange('Écriture comptabilisée', `${entry.reference || entry.description || 'Écriture'} - ${entry.totalDebit?.toLocaleString('fr-FR')} CDF`);

    if (navigator.onLine) {
      try {
        await journalEntriesApi.post(id);
      } catch (apiError) {
        console.error('API post failed, rolling back:', apiError);
        await rollbackEntry(originalEntry);
        throw new Error('Échec de la comptabilisation. L\'écriture a été restaurée à son état initial.');
      }
    } else {
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: updatedEntry
      });
    }
  };

  /**
   * Validation directe (raccourci): draft → posted
   * Pour les petites structures sans workflow de validation
   */
  const validateAndPostEntry = async (id: string): Promise<void> => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entry = safeEntries.find(e => e.id === id);
    
    if (!entry) {
      throw new Error(`Écriture ${id} non trouvée`);
    }

    if (entry.status === 'posted') {
      throw new Error('Cette écriture est déjà comptabilisée');
    }

    // Vérifier que l'écriture est équilibrée
    if (entry.totalDebit !== entry.totalCredit) {
      throw new Error('L\'écriture doit être équilibrée (débit = crédit)');
    }

    // Vérifier que l'exercice est toujours ouvert
    if (currentFiscalYear?.status !== 'open') {
      throw new Error('L\'exercice comptable est clôturé');
    }

    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entry };
    
    const updatedEntry: JournalEntry = {
      ...entry,
      status: 'posted'
    };

    await IndexedDBService.update('journal_entries', updatedEntry);
    setEntries(prev => prev.map(e => e.id === id ? updatedEntry : e));

    // Logger l'activité
    logJournalStateChange('Validation directe', `${entry.reference || entry.description || 'Écriture'} - Comptabilisée directement - ${entry.totalDebit?.toLocaleString('fr-FR')} CDF`);

    if (navigator.onLine) {
      try {
        await journalEntriesApi.post(id);
      } catch (apiError) {
        console.error('API post failed, rolling back:', apiError);
        await rollbackEntry(originalEntry);
        throw new Error('Échec de la validation. L\'écriture a été restaurée à son état initial.');
      }
    } else {
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'journal_entries',
        data: updatedEntry
      });
    }
  };

  /**
   * Obtenir les actions disponibles selon l'état
   */
  const getAvailableActions = (entry: JournalEntry): string[] => {
    const actions: string[] = ['view'];
    const isFiscalYearOpen = currentFiscalYear?.status === 'open';

    switch (entry.status) {
      case 'draft':
        if (isFiscalYearOpen) {
          actions.push('edit', 'delete', 'submit', 'validateAndPost');
        }
        break;
      case 'pending':
        if (isFiscalYearOpen) {
          actions.push('approve', 'reject');
        }
        break;
      case 'approved':
        if (isFiscalYearOpen) {
          actions.push('post');
        }
        break;
      case 'posted':
        // Aucune action de modification possible
        break;
    }

    return actions;
  };

  return {
    entries,
    loading,
    error,
    pagination,
    addEntry,
    updateEntry,
    deleteEntry,
    uploadAttachment,
    downloadAttachment,
    loadEntries,
    setPage,
    setPageSize,
    // Fonctions de transition d'état
    submitEntry,
    approveEntry,
    rejectEntry,
    postEntry,
    validateAndPostEntry,
    getAvailableActions
  };
}